import { Link } from 'react-router-dom';
import { Settings, Plus, Loader2, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';
import { useShortcuts, useUpsertShortcut, type DentalShortcutRow } from '@/hooks/useDentalShortcuts';
import { useProcedureCodes, type DentalProcedureCodeRow } from '@/hooks/useProcedureCodes';
import { ShortcutButton } from './ShortcutButton';
import { ProcedureCodePicker } from './ProcedureCodePicker';
import { useState } from 'react';

interface ShortcutsPanelProps {
  clinicId: string;
  activeShortcutId: string | null;
  onShortcutToggle: (id: string) => void;
  className?: string;
}

export function ShortcutsPanel({
  clinicId,
  activeShortcutId,
  onShortcutToggle,
  className,
}: ShortcutsPanelProps) {
  const { t } = useTranslation();
  const { data: shortcuts, isLoading } = useShortcuts(clinicId);
  const { data: allCodes } = useProcedureCodes();
  const upsert = useUpsertShortcut(clinicId);
  const [showPicker, setShowPicker] = useState(false);

  const codeMap = new Map<string, DentalProcedureCodeRow>(allCodes?.map((c) => [c.code, c]) ?? []);

  const handleAddShortcut = async (row: DentalProcedureCodeRow) => {
    try {
      const nextOrder = (shortcuts?.length ?? 0) + 1;
      await upsert.mutateAsync({
        label: row.name_da.slice(0, 20),
        procedureCode: row.code,
        color: '#6366f1',
        displayOrder: nextOrder,
      });
      toast.success(t('dental.shortcuts.addShortcut'));
      setShowPicker(false);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Fejl ved tilføjelse af lynvalg';
      toast.error(msg);
    }
  };

  return (
    <div
      role="region"
      aria-label="Lynvalg"
      className={cn(
        'flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <Zap className="h-4 w-4 text-indigo-500 shrink-0" />
          <span className="text-sm font-semibold text-slate-800 dark:text-white">
            {t('dental.shortcuts.title')}
          </span>
        </div>
        <Link
          to="/settings/dental-shortcuts"
          target="_blank"
          rel="noopener noreferrer"
          className="rounded p-1 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          title={t('dental.shortcuts.openSettings')}
        >
          <Settings className="h-4 w-4" />
        </Link>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="grid grid-cols-3 gap-1.5">
            {Array.from({ length: 9 }).map((_, i) => (
              <div
                key={i}
                className="h-10 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse"
              />
            ))}
          </div>
        ) : !shortcuts || shortcuts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 px-3 text-center">
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {t('dental.shortcuts.emptyState')}
            </p>
            <Link
              to="/settings/dental-shortcuts"
              target="_blank"
              className="mt-2 text-xs text-indigo-500 hover:text-indigo-600 underline"
            >
              {t('dental.shortcuts.openSettings')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1.5">
            {shortcuts.map((sc: DentalShortcutRow) => (
              <ShortcutButton
                key={sc.id}
                shortcut={sc}
                isActive={activeShortcutId === sc.id}
                onClick={(s) => onShortcutToggle(s.id)}
                tooltipName={codeMap.get(sc.procedure_code)?.name_da}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-slate-100 dark:border-slate-800 p-2">
        {activeShortcutId && (
          <button
            type="button"
            onClick={() => onShortcutToggle(activeShortcutId)}
            className="mb-1.5 w-full rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            {t('dental.shortcuts.deactivateShortcut')}
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          disabled={upsert.isPending}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-indigo-300 dark:border-indigo-700 px-2 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors disabled:opacity-50"
        >
          {upsert.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          {t('dental.shortcuts.addNew')}
        </button>
      </div>

      {showPicker && (
        <ProcedureCodePicker
          asModal
          onSelect={handleAddShortcut}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
